import { Injectable } from '@angular/core';

/**
 * Our abstraction over localStorage,
 *  - typed and versioned
 *  - with safe storage of organization-related items
 *  - with try/catch on localStorage access
 */

interface GlobalContainer<T> {
    version: number;
    data: T;
}

interface OrganizationContainer<T> {
    orgId: string;
    version: number;
    data: T;
}

export enum LocalStorageKeys {
    currentUser = 'currentUser',
    cockpitState = 'cockpitState',
    config = 'config',
    prevSkin = 'prevSkin',
    HEADERS_LOCALSTORAGE_KEY = 'consolidated_view_columns',
    sudoMod = 'sudoMod',
    dcarlist = 'dcarlist_',
    layoutConfig = 'layoutConfig',
    localeUser = 'localeUser',
}

@Injectable({
    providedIn: 'root',
})
export class StorageService {
    /**
     * Get a item from storage, globally (not related to an organization)
     * @param key
     * @param version Expected version, discard if found incorrect
     * @returns T if found for the version
     */
    public getGlobalItem<T>(mainKey: LocalStorageKeys, version: number = 1, keyPrecision?: string): T | undefined {
        let key: string;
        if (keyPrecision) {
            key = mainKey + keyPrecision;
        } else {
            key = mainKey;
        }

        try {
            const json = localStorage.getItem(key);
            if (json) {
                try {
                    const container = <GlobalContainer<T>>JSON.parse(json);
                    if (container.version === version && 'data' in container) {
                        return container.data;
                    }
                } catch (err) {
                    console.error(`getGlobalItem() Error on JSON parse ${key}`);
                }
                // Cleanup bad json or version missmatch
                localStorage.removeItem(key);
            }
        } catch (err) {
            console.warn('Cannot read from localStorage: ' + err);
        }
        return undefined;
    }

    /**
     * Store an item in storage, globally (not related to an organization)
     * @param key
     * @param data
     */
    public setGlobalItem<T>(mainKey: LocalStorageKeys, data: T, version: number = 1, keyPrecision?: string) {
        let key: string;

        if (keyPrecision) {
            key = mainKey + keyPrecision;
        } else {
            key = mainKey;
        }
        const container: GlobalContainer<T> = { version, data };
        try {
            localStorage.setItem(key, JSON.stringify(container));
        } catch (err) {
            // Could be QuotaExceeded
            console.warn('Cannot write to localStorage: ' + err);
        }
    }

    /**
     * Get an item from storage for an organization
     * @param organisationId
     * @param key
     * @param version Expected version, discard if found incorrect
     * @returns T if found for the organization & version
     */
    public getOrganizationItem<T>(organisationId: string, key: LocalStorageKeys, version: number = 1): T | undefined {
        try {
            const json = localStorage.getItem(key);
            if (json) {
                try {
                    const container = <OrganizationContainer<T>>JSON.parse(json);
                    if (container.orgId === organisationId && container.version === version && 'data' in container) {
                        return container.data;
                    }
                } catch (err) {
                    console.error(`getOrganizationItem() Error on JSON parse ${key}`);
                }
                // Cleanup bad json or version missmatch
                localStorage.removeItem(key);
            }
        } catch (err) {
            console.warn('Cannot read from localStorage: ' + err);
        }
        return undefined;
    }

    /**
     * Store an item in storage for an organization
     * @param key
     * @param data
     * @param version
     */
    public setOrganizationItem<T>(organizationId: string, key: LocalStorageKeys, data: T, version: number = 1) {
        const container: OrganizationContainer<T> = {
            orgId: organizationId,
            version,
            data,
        };
        try {
            localStorage.setItem(key, JSON.stringify(container));
        } catch (e) {
            // Could be QuotaExceeded
            console.warn('Cannot write to localStorage: ' + e);
        }
    }

    /**
     * Remove a key from storage
     * @param key
     */
    public removeItem(key: LocalStorageKeys) {
        try {
            localStorage.removeItem(key);
        } catch {
            // ignore
        }
    }
}
